import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import authSession from '@/utils/authSession';
import { request, refreshAccessToken } from '@/utils/request';

// 默认接收实时更新提示；连接失败仍保留 HTTP 轮询，可显式关闭 WebSocket。
const useChatEvents = (conversationId, onUpdate, onUnavailable) => {
  const callbacks = useRef({ onUpdate, onUnavailable }); callbacks.current = { onUpdate, onUnavailable };
  const { isAuthenticated, sessionVersion } = useSelector(state => state.user);
  useEffect(() => {
    if (process.env.REACT_APP_CHAT_WEBSOCKET_ENABLED === 'false' || !isAuthenticated || !window.WebSocket || !Number.isSafeInteger(Number(conversationId))) return;
    let active = true; let unavailable = false; let socket; let timer; let attempts = 0;
    const connect = () => {
      if (!active || unavailable || document.hidden || (socket && socket.readyState <= 1)) return;
      const base = new URL(request.defaults.baseURL, window.location.origin);
      const endpoint = new URL('/ws/chat/', base); endpoint.protocol = base.protocol === 'https:' ? 'wss:' : 'ws:';
      socket = new WebSocket(endpoint.href);
      const connection = socket;
      socket.onopen = () => { if (active && socket === connection) connection.send(JSON.stringify({ type: 'authenticate', access: authSession.getAccessToken() })); };
      socket.onmessage = event => {
        if (!active || socket !== connection) return;
        let data; try { data = JSON.parse(event.data); } catch { return; }
        if (data.type === 'authenticated') connection.send(JSON.stringify({ type: 'subscribe', conversation_id: Number(conversationId) }));
        if (String(data.conversation_id) !== String(conversationId)) return;
        if (data.type === 'subscribed') { attempts = 0; callbacks.current.onUpdate(); }
        if (data.type === 'conversation.updated') callbacks.current.onUpdate();
        if (data.type === 'conversation.unavailable' || (data.type === 'error' && data.code === 'not_found')) { unavailable = true; callbacks.current.onUnavailable(); connection.close(); }
      };
      socket.onclose = async event => {
        if (!active || unavailable || [4400, 4403].includes(event.code)) return;
        if (event.code === 4401) { try { await refreshAccessToken(); } catch { return; } }
        if (active && socket === connection) timer = setTimeout(connect, Math.min(60000, 1000 * 2 ** Math.min(attempts++, 6)));
      };
    };
    const visibility = () => {
      clearTimeout(timer);
      if (document.hidden) { if (socket) { socket.onclose = null; socket.close(); socket = null; } }
      else if (!socket || socket.readyState > 1) connect();
    };
    const refreshed = () => {
      clearTimeout(timer);
      if (socket) { socket.onclose = null; socket.close(); socket = null; }
      connect();
    };
    connect(); document.addEventListener('visibilitychange', visibility);
    window.addEventListener('ipartner-access-refreshed', refreshed);
    return () => { active = false; clearTimeout(timer); document.removeEventListener('visibilitychange', visibility); window.removeEventListener('ipartner-access-refreshed', refreshed); if (socket) { socket.onclose = null; socket.close(); } };
  }, [conversationId, isAuthenticated, sessionVersion]);
};
export default useChatEvents;
